/**
 * @file API service — plantCatalogPublicService.
 * GET /plant-capacities — public, no auth.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";
import type { PlantCapacityOption } from "../interfaces/formInterfaces";

/** Type definition — Plant Capacities Response. */
export type PlantCapacitiesResponse = {
  unitCategory: string;
  capacities: PlantCapacityOption[];
};

/** Exported function — fetch Plant Capacities By Material Id. */
export async function fetchPlantCapacitiesByMaterialId(
  materialId: number
): Promise<PlantCapacitiesResponse> {
  if (!Number.isFinite(materialId) || materialId <= 0) {
    return { unitCategory: "", capacities: [] };
  }

  const res = await axiosClient.get(API_URLS.PLANT_CAPACITIES, {
    params: { materialId },
  });

  const data = res?.data?.data ?? res?.data;
  const capacities = data?.capacities;
  const unitCategory = String(data?.unitCategory ?? "").trim();

  if (!Array.isArray(capacities)) {
    return { unitCategory, capacities: [] };
  }

  const list = capacities
    .map((row: Record<string, unknown>) => ({
      id: Number(row.id),
      materialId: row.materialId != null ? Number(row.materialId) : undefined,
      category: String(row.category ?? "").trim(),
      capacityLabel: String(row.capacityLabel ?? "").trim(),
      unitCategory: String(row.unitCategory ?? unitCategory).trim(),
      sortOrder: Number(row.sortOrder) || 0,
    }))
    .filter((row) => Number.isFinite(row.id) && row.id > 0 && row.category)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return { unitCategory, capacities: list };
}
